export default class ImageNormalizerController {
  constructor(options) {
    // Set the default settings
    this.settings = {
      minScale: 0.5,
      maxScale: 1,
      ...options
    };

    // Store all the images and aspect ratios
    this.images = new Map();
    // Track how many images have loaded
    this.loadedImagesCount = 0;
    // Track total images added
    this.totalImagesCount = 0;

    // Set up a resize timeout to debounce the resize event
    this.resizeTimeout = null;

    // Set up a resize observer
    this.SizeObserver = new ResizeObserver(() => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.update(), 100);
    });
  }
  async add() {
    let images = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    if (!Array.isArray(images)) return;

    // Update total image count
    this.totalImagesCount += images.length;
    const onLoad = image => {
      const parent = image.parentElement;
      // If there is no parent, or the image is not an HTMLImageElement, return
      if (!parent || !(image instanceof HTMLImageElement)) return;
      // Get the container
      const container = parent.parentElement || null;

      // Observe the parent for resize events
      this.SizeObserver.observe(parent);

      // Get the image data
      const {
        displayedSurfaceArea,
        parentAspectRatio
      } = this.getImageData(image, parent);

      // If the displayed surface area is less than 1, return
      if (displayedSurfaceArea < 1) return;

      // Add the image's aspect ratio to the map
      this.images.set(image, {
        displayedSurfaceArea,
        parent,
        parentAspectRatio,
        container
      });

      // Track how many images have loaded
      this.loadedImagesCount++;

      // Once all images are loaded, call normalize()
      if (this.loadedImagesCount === this.totalImagesCount) {
        // Normalize once all images are loaded
        this.normalize();
      }
    };

    // Create an array of promises for image loading
    const loadPromises = images.map(image => {
      // Make sure the image is an HTMLImageElement
      if (!(image instanceof HTMLImageElement)) return Promise.resolve();

      // If the image is already loaded, call the onLoad function
      if (image.complete) {
        onLoad(image);
        return Promise.resolve();
      } else {
        // Add the load event listener
        return new Promise(resolve => {
          image.addEventListener('load', () => {
            onLoad(image);
            resolve();
          });
        });
      }
    });

    // Wait for all images to load
    await Promise.all(loadPromises);
  }
  getImageData(image, parent) {
    // Get the image's natural size
    const {
      naturalWidth,
      naturalHeight,
      height,
      width,
      clientHeight,
      clientWidth
    } = image;
    const parentWidth = parent.clientWidth;
    const parentHeight = parent.clientHeight;
    const parentAspectRatio = parentWidth / parentHeight;
    const widthValue = naturalWidth || width || clientWidth;
    const heightValue = naturalHeight || height || clientHeight;

    // Determine how the image is scaled inside the parent
    const widthRatio = parentWidth / widthValue;
    const heightRatio = parentHeight / heightValue;

    // Maintain aspect ratio
    const scaleFactor = Math.min(widthRatio, heightRatio);

    // Calculate the actual displayed image size
    const displayedWidth = widthValue * scaleFactor;
    const displayedHeight = heightValue * scaleFactor;
    const displayedSurfaceArea = displayedWidth * displayedHeight;
    return {
      displayedSurfaceArea,
      parentAspectRatio
    };
  }
  normalize() {
    if (!this.images.size) return;
    let minSurfaceArea = Infinity;
    this.images.forEach((data, image) => {
      // console.log(data, image);
      const {
        displayedSurfaceArea
      } = data;
      minSurfaceArea = Math.min(minSurfaceArea, displayedSurfaceArea);
    });
    // console.log('ImageNormalizer: Normalizing images', minSurfaceArea);

    requestAnimationFrame(() => {
      this.images.forEach((data, image) => {
        const {
          displayedSurfaceArea
        } = data;

        // Calculate the scale factor
        let scale = Math.sqrt(minSurfaceArea / displayedSurfaceArea);

        // Clamp between 0.5 and 1
        scale = Math.max(0.5, Math.min(1, scale));

        // Add the loaded class to the image
        image.classList.add('loaded');

        // If the scale is 1, no need to transform the image
        if (scale === 1) return;

        // Otherwise, transform the image
        image.style.scale = scale;
        this.distributeEvenly();
      });
    });
  }
  distributeEvenly() {
    if (!this.images.size) return;
    let groupContainer = null;
    let groupContainerRect = null;
    let groupContainerWidth = 0;
    this.images.forEach((data, image) => {
      const {
        parent,
        container
      } = data;
      if (!container) return;
      const parentRect = parent.getBoundingClientRect();
      if (!groupContainer || container !== groupContainer) {
        groupContainer = container;
        groupContainerRect = groupContainer.getBoundingClientRect();
        groupContainerWidth = groupContainerRect.width;
      }
      const offset = parentRect.left - groupContainerRect.left;
      const transformOrigin = `${offset / groupContainerWidth * 100}% 50%`;
      image.style.transformOrigin = transformOrigin;
    });
  }
  update() {
    // If there are no images, return
    if (this.images.size === 0) return;

    // Check to see if we should normalize the images again
    let shouldNormalize = false;
    for (const [image, data] of this.images) {
      // Grab the existing parent aspect ratio
      const {
        parent,
        parentAspectRatio
      } = data;
      // Calculate the parent's aspect ratio again
      const newParentAspectRatio = parent.clientWidth / parent.clientHeight;

      // If the parent's aspect ratio has changed, we need to normalize the images again
      if (newParentAspectRatio !== parentAspectRatio) {
        // Set the flag to true
        shouldNormalize = true;
        // Break the loop
        break;
      }
    }

    // If we need to normalize the images, update all the image data, then call normalize()
    if (shouldNormalize) {
      // Update the images displyed surface area
      this.images.forEach((data, image) => {
        const {
          displayedSurfaceArea,
          parentAspectRatio
        } = this.getImageData(image, data.parent);
        data.displayedSurfaceArea = displayedSurfaceArea;
        data.parentAspectRatio = parentAspectRatio;
      });

      // Normalize the images
      this.normalize();
    }
    ;
  }
}